<script setup lang="ts">
import { fournirRegimeFiscal } from "~/composables/useRegimeFiscal";
import { MOYENS_PAIEMENT_LOCAUX } from "@donoor/utils";
import { champNomAffiche } from "~/utils/champNomDonateur";
/**
 * DonateCheckoutApp — Corps réutilisable de la page de don personnalisée.
 *
 * Extrait de `pages/pay/[slug].vue` pour être partagé SANS duplication entre :
 *   - `/pay/[slug]`   → `mode="pay"`   (comportement historique inchangé)
 *   - `/embed/[slug]` → `mode="embed"` (widget de don embarqué en iframe cross-site)
 *
 * Le mode `pay` reproduit à l'identique l'ancienne page (aucune régression
 * attendue). Le mode `embed` ne diverge que sur 3 points, tous gardés par
 * `props.mode === "embed"` :
 *   1. Fin de paiement → `postMessage('donoor-embed:payment_completed')` vers
 *      l'hôte AU LIEU d'un `router.push('/donation-success')` (on reste dans
 *      l'iframe, l'hôte décide quoi afficher via `window.Donoor.onDonation`).
 *   2. Auto-resize : un ResizeObserver pousse la hauteur du contenu à l'hôte
 *      (`donoor-embed:resize`) — ce que HelloAsso ne fait pas.
 *   3. Thème hérité : l'hôte peut pousser des couleurs (`donoor-embed:theme`),
 *      appliquées via les mêmes CSS vars que le mode pay.
 *
 * La logique bas niveau Stripe/PayPal est déléguée à `@donoor/checkout` via
 * les wrappers `useStripeDonationCheckout` / `usePaypalDonationCheckout`.
 */
import { isPaypalCardFieldsUsable, resolveCardProvider } from "@donoor/checkout";
import DonateMoyensLocaux from "~/components/donate/DonateMoyensLocaux.vue";
import { useMoyensLocauxDon } from "~/composables/payment/useMoyensLocauxDon";
import { cleTexteTitulaire } from "~/utils/texteTitulaireMoyenLocal";
import { isValidEmail } from "@donoor/validators";
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useLocalePath } from "#i18n";
import { logger } from "~/utils/logger";
import { extractApiError } from "~/utils/apiError";
import { paymentLinksApi } from "~/services/api/paymentLinks";
import { recuFiscalAnnonce } from "~/utils/recuFiscal";
import {
  EMBED_MESSAGE,
  isValidHostOrigin,
  sanitizeCssColor,
} from "~/utils/embed";

// Type partagé pour les custom fields configurés sur le payment link.
interface CustomField {
  type: "fullname" | "email" | "message";
  required: boolean;
  label: string;
}

const props = withDefaults(
  defineProps<{
    /** Slug de la page de don (identique à /pay/[slug]). */
    slug: string;
    /** `pay` = page classique ; `embed` = iframe cross-site. */
    mode?: "pay" | "embed";
    /**
     * (embed) Origine de l'hôte qui embarque l'iframe, transmise par embed.js
     * via `?host=`. Sert de cible pour les postMessage sortants et de filtre
     * pour les entrants. Validée contre les domaines autorisés côté serveur
     * (header frame-ancestors) ; ici on ne fait qu'une validation de forme.
     */
    hostOrigin?: string | null;
  }>(),
  {
    mode: "pay",
    hostOrigin: null,
  },
);

const isEmbed = computed(() => props.mode === "embed");

const { t } = useI18n();
const localePath = useLocalePath();
const route = useRoute();
const router = useRouter();
const slug = props.slug;
const config = useRuntimeConfig();

// Mode aperçu (iframe dashboard) — spécifique à /pay. En embed, jamais actif.
// Les paramètres `previewXxx` ne s'appliquent qu'en preview.
const isPreviewMode = computed(
  () => !isEmbed.value && route.query.preview === "true",
);

usePreviewScrollbar(isPreviewMode);

// (embed) Origine hôte validée en forme — cible des postMessage sortants.
const resolvedHostOrigin = computed<string | null>(() => {
  if (!isEmbed.value) return null;
  const raw = props.hostOrigin ?? (route.query.host as string | undefined) ?? null;
  return isValidHostOrigin(raw) ? raw : null;
});

// ============================================
// SSR — données + SEO. En embed : même fetch, mais pas de SEO/OG/canonical.
// ============================================
const cacheBuster = route.query._t as string | undefined;
const { ssrPaymentData, ssrPaymentPage } = await usePaySlugSeo(
  slug,
  cacheBuster,
  { mode: props.mode },
);

// ============================================
// Checkout — Stripe + PayPal via @donoor/checkout
// ============================================

// Montant du don à Donoor (0 en preview pour ne pas affecter le calcul ;
// en embed comme en pay classique → vrai pourboire calculé).
const donateToUsAmount = ref<number>(
  isPreviewMode.value ? 0 : getDonoorTipAmount(10),
);

const donationData = useDonationData(ref(slug), {
  apiPath: "/api/public/payment-link",
  skipSuffix: true,
  isPaymentLink: true,
});

// En preview, si le slug n'existe pas en base, forcer l'affichage.
const isPreviewWithoutData =
  isPreviewMode.value && !ssrPaymentData.value?.success;
if (isPreviewWithoutData) {
  donationData.isQrCodeValid.value = true;
}
watch(donationData.isQrCodeValid, (val) => {
  if (isPreviewWithoutData && !val) {
    donationData.isQrCodeValid.value = true;
  }
});

const paymentForm = usePaymentForm();

// Estimation « couvrir les frais » provider-aware (Karim 2026-08-15) : si PayPal
// sera le moyen utilisé → tarif PayPal appris ; sinon tarif Stripe (inchangé).
const { estimateProvider, paypalFeePercent, paypalFeeFixed } =
  usePaypalFeeEstimate(donationData.paymentProviders, paymentForm.isSubscription);

const calculations = usePaymentCalculations({
  amount: computed(() => paymentForm.form.value.amount),
  includeFees: paymentForm.includeFees,
  donoorFeeRate: donationData.donoorFeeRate,
  isSubscription: paymentForm.isSubscription,
  subscriptionPeriod: paymentForm.subscriptionPeriod,
  donateToUsAmount,
  paymentProvider: estimateProvider,
  paypalFeePercent,
  paypalFeeFixed,
});

// « L'association reçoit … » : le don MOINS les frais quand le donateur ne
// les couvre pas (bug 14/10/2026 : on affichait le don entier), avec le
// détail de ce qui est retiré (frais du prestataire, commission Donoor).
const detailAssociation = useNetAssociationEstime({
  montant: calculations.finalAmount,
  soutienDonoor: donateToUsAmount,
  couvreFrais: paymentForm.includeFees,
  tauxDonoor: donationData.donoorFeeRate,
  prestataire: estimateProvider,
  paypalPourcent: paypalFeePercent,
  paypalFixe: paypalFeeFixed,
});

// `contextIds` : attribue la transaction au QR code quand le slug = UUID d'un
// QR physique. Getter → lu paresseusement au checkout (après résolution SSR).
const qrContextIds = {
  get qr_code_id(): string | undefined {
    return donationData.resolvedQrCodeId.value ?? undefined;
  },
};

// successUrl : en mode pay → redirection classique /donation-success.
// En embed → on ne quitte PAS l'iframe (l'hôte gère l'après-don), mais on
// fournit quand même une URL valide au SDK (fallback 3DS/redirect wallet).
const buildSuccessUrl = (email?: string) => {
  const params = new URLSearchParams({ slug });
  if (email) params.set("email", email);
  if (isEmbed.value) params.set("embed", "1");
  return `/donation-success?${params.toString()}`;
};

// ─────────────────────────────────────────────────────────────────────────
// Stripe Checkout — factorisé via useStripeDonationCheckout.
// ─────────────────────────────────────────────────────────────────────────
const stripeDonation = useStripeDonationCheckout({
  couleur: () => primaryColor.value,
  paymentForm,
  calculations,
  organizationId: donationData.organizationId,
  stripeAccountId: donationData.stripeAccountId,
  donateUs: donationData.donateUs,
  donateToUsAmount,
  enableSubscriptions: true,
  contextIds: qrContextIds,
  extraBodyBuilder: () => {
    if (
      customFields.value &&
      Object.keys(customFieldValues.value).length > 0
    ) {
      return { custom_fields: JSON.stringify(customFieldValues.value) };
    }
    return {};
  },
  successUrl: buildSuccessUrl,
});

const checkout = stripeDonation.checkout;
const currentPaymentIntentId = stripeDonation.currentPaymentIntentId;
// Provenance du lien de départ (utm, gclid…) → webhooks de l'association.
// Lecture seule de l'identifiant, envoi isolé (cf. useProvenance).
suivreProvenancePaiement(currentPaymentIntentId);

const isDev = computed(() => config.public.mode === "dev");

// ─────────────────────────────────────────────────────────────────────────
// PayPal Checkout — factorisé via usePaypalDonationCheckout.
// ─────────────────────────────────────────────────────────────────────────
const paypalDonation = usePaypalDonationCheckout({
  couleur: () => primaryColor.value,
  paymentProviders: donationData.paymentProviders,
  paymentForm,
  calculations,
  donateUs: donationData.donateUs,
  donateToUsAmount,
  organizationId: donationData.organizationId,
  contextIds: qrContextIds,
  successUrl: buildSuccessUrl,
});

const paypalCheckout = paypalDonation.checkout;
const isPaypalOnlyMode = paypalDonation.isPaypalOnlyMode;
const showPayPalAlternative = paypalDonation.showPayPalAlternative;

// Provider du FORMULAIRE CARTE (Karim 2026-08-30) : `preferred_provider` = où
// l'asso veut recevoir l'argent (bouton PayPal, wallets), mais il n'impose les
// CardFields PayPal que si PayPal sait les rendre (capability PPCP_CUSTOM).
// Sinon, Stripe actif → carte Stripe. Jamais de cadre vide. Règle unique dans
// `resolveCardProvider` (@donoor/checkout) — ne PAS la réimplémenter ici.
const usesPaypalCard = computed(() => {
  const pp = donationData.paymentProviders.value;
  return (
    resolveCardProvider({
      stripe: pp.stripe.charges_enabled,
      paypal: pp.paypal.charges_enabled,
      paypalCardFields: isPaypalCardFieldsUsable(pp.paypal),
      preferred: pp.preferred_provider,
      isRecurring: paymentForm.isSubscription.value,
    }) === "paypal"
  );
});
const usesPaypalWallets = computed(
  () => isPaypalOnlyMode.value || usesPaypalCard.value,
);

const { mountPrimaryWallets } = useWalletsMount({
  stripeCheckout: checkout,
  paypalCheckout,
  isPaypalOnlyMode,
  usesPaypalWallets,
});

// ─────────────────────────────────────────────────────────────────────────
// Alias et helpers pour le template
// ─────────────────────────────────────────────────────────────────────────
const hasSSRData = !!ssrPaymentData.value?.success;
// Mention du pays (66 %, 30 % en Belgique, déduction au Luxembourg) — 2026-10-30.
fournirRegimeFiscal(() => (ssrPaymentData.value?.data as { fiscalRegime?: string | null } | undefined)?.fiscalRegime);
const pageLoading = computed(() =>
  hasSSRData ? false : donationData.isLoading.value,
);
const isQrCodeValid = donationData.isQrCodeValid;
const qrCodeError = donationData.qrCodeError;
const errorTitle = donationData.errorTitle;
const stripeInitError = computed(() => checkout.cardFormError.value !== null);
const stripeErrorMessage = computed(
  () => checkout.cardFormError.value?.message || "",
);

const donationInfo = computed(() => {
  const base = donationData.donationInfo.value;
  if (
    isPreviewMode.value &&
    !ssrPaymentData.value?.success &&
    previewOrgName.value
  ) {
    return { ...base, organization: previewOrgName.value };
  }
  return base;
});
const organizationLogoUrl = donationData.organizationLogoUrl;
/**
 * Message personnalisé du QR code — saisi depuis le dashboard comme depuis
 * l'app iOS/Android, il n'était affiché NULLE PART : il arrivait bien du
 * serveur, mais aucun template ne le rendait (Karim 2026-08-26).
 *
 * Il a sa propre place dans le hero, en citation, et ne se substitue pas au
 * sous-titre de la page de paiement : les deux peuvent coexister.
 */
const messageQrCode = donationData.qrCustomMessage;
const isLoading = paymentForm.isLoading;
const form = paymentForm.form;
const suggestedAmounts = paymentForm.suggestedAmounts;
const donationType = paymentForm.donationType;
const subscriptionPeriod = paymentForm.subscriptionPeriod;
const subscriptionPeriods = paymentForm.subscriptionPeriods;
const includeFees = paymentForm.includeFees;
const touched = paymentForm.touched;
const errors = paymentForm.errors;
const isSubscription = paymentForm.isSubscription;
const finalAmount = calculations.finalAmount;
const feesAmount = calculations.feesAmount;
const stripeFeesAmount = calculations.stripeFeesAmount;
const donoorFeesAmount = calculations.donoorFeesAmount;
const finalAmountWithDiscount = calculations.finalAmountWithDiscount;
const formatAmount = calculations.formatAmount;
const validateEmail = paymentForm.validateEmail;
const validateName = paymentForm.validateName;
const validateAmount = paymentForm.validateAmount;
const donateUs = donationData.donateUs;
const isPremiumPlan = donationData.isPremiumPlan;

// ─────────────────────────────────────────────────────────────────────────
// Surcharges visuelles & comportementales — factorisées dans
// usePaymentLinkPreview (postMessage preview + query + SSR).
// ─────────────────────────────────────────────────────────────────────────
const preview = usePaymentLinkPreview({
  isPreviewMode,
  ssrPaymentPage,
  ssrShowFiscalInfo: recuFiscalAnnonce(ssrPaymentData.value?.data),
});
const {
  impactOptions,
  primaryColor: ssrPrimaryColor,
  backgroundColor,
  buttonStyle,
  submitText,
  youtubeUrl,
  customTitle,
  customSubtitle,
  bannerUrl,
  subscriptionsEnabled,
  showFeesOption,
  showFiscalInfo,
  customFields,
  previewOrgName,
  previewGoal,
  previewEndDate,
  previewShowGoalProgress,
  previewRaised,
  previewAmounts,
  previewDefaultAmountIndex,
} = preview;

// (embed) Couleur primaire éventuellement héritée du site hôte (thème auto).
// Priorité : couleur poussée par l'hôte > couleur SSR de la page de don.
const hostPrimaryColor = ref<string | null>(null);
const primaryColor = computed(
  () => hostPrimaryColor.value ?? ssrPrimaryColor.value,
);

// null = pas configuré = tout activé par défaut (message + email + nom).
const isFieldRequired = (type: CustomField["type"]) =>
  !customFields.value || customFields.value.some((f) => f.type === type);

const isMessageEnabled = computed(() => isFieldRequired("message"));
const customFieldsWithoutMessage = computed(() =>
  customFields.value ? customFields.value.filter((f) => f.type !== "message") : null,
);
const customFieldValues = ref<Record<string, string>>({});

const isValidForm = computed(() => {
  if (parseFloat(form.value.amount) <= 0) return false;
  if (isFieldRequired("email")) {
    const ok =
      form.value.donorEmail.trim() !== "" &&
      isValidEmail(form.value.donorEmail);
    if (!ok) return false;
  }
  if (isFieldRequired("fullname") && !form.value.isAnonymous) {
    if (!form.value.donorName.trim()) return false;
  }
  return true;
});

const validateAllFields = () => {
  validateAmount();
  if (isFieldRequired("email")) validateEmail();
  if (isFieldRequired("fullname")) validateName();
};

const {
  darkenColor,
  goalAmount,
  raisedAmount,
  showGoalProgress,
  daysRemaining,
  feesText,
  subscriptionSuffix,
  displayAmount,
} = usePaySlugDisplay({
  isPreviewMode,
  ssrPaymentData,
  ssrPaymentPage,
  previewGoal,
  previewRaised,
  previewEndDate,
  previewShowGoalProgress,
  previewAmounts,
  previewDefaultAmountIndex,
  paymentForm,
  donationData,
  finalAmountWithDiscount,
  formatAmount,
  youtubeUrl,
});

// ─────────────────────────────────────────────────────────────────────────
// Soumission & WebSocket
// ─────────────────────────────────────────────────────────────────────────
const localError = ref<string | null>(null);

const globalError = computed<string | null>(() => {
  if (localError.value) return localError.value;
  const paypalErr = paypalCheckout.error.value;
  if (paypalErr && paypalErr.type === "card_error") return paypalErr.message;
  const stripeErr = checkout.error.value;
  if (stripeErr && stripeErr.type === "card_error") return stripeErr.message;
  return null;
});

const dismissedWalletError = ref<string | null>(null);
const walletError = computed<string | null>(() => {
  const paypalErr = paypalCheckout.error.value;
  if (paypalErr && paypalErr.type === "wallet_error") {
    if (paypalErr.message === dismissedWalletError.value) return null;
    return paypalErr.message;
  }
  const stripeErr = checkout.error.value;
  if (stripeErr && stripeErr.type === "wallet_error") {
    if (stripeErr.message === dismissedWalletError.value) return null;
    return stripeErr.message;
  }
  return null;
});
function dismissWalletError() {
  if (walletError.value) dismissedWalletError.value = walletError.value;
}
const paymentSocket = usePaymentSocket();
let hasRedirected = false;

const reloadPage = () => {
  if (typeof window !== "undefined") {
    window.location.reload();
  }
};

// (embed) Émet vers l'hôte la fin de paiement. Ne quitte jamais l'iframe.
function emitEmbedPaymentCompleted(payload: {
  transactionId?: string | number | null;
  paymentIntentId?: string | null;
  amount?: number | string | null;
}) {
  if (!isEmbed.value || typeof window === "undefined") return;
  const target = resolvedHostOrigin.value;
  if (!target) return; // hôte inconnu/invalide → on n'émet pas (sécurité).
  window.parent.postMessage(
    {
      type: EMBED_MESSAGE.PAYMENT_COMPLETED,
      data: {
        transactionId: payload.transactionId ?? null,
        amount: payload.amount ?? finalAmount.value,
        currency: "eur",
        recurring: isSubscription.value,
        donorEmail: form.value.donorEmail || null,
      },
    },
    target,
  );
}

const handleSubmit = async () => {
  const requiresEmail = isFieldRequired("email");
  if (requiresEmail) validateEmail();
  if (!isValidForm.value || (requiresEmail && errors.email)) {
    validateAllFields();
    localError.value = t("don.checkoutApp.erreurValidation");
    return;
  }

  isLoading.value = true;
  localError.value = null;

  try {
    let result: { error?: { message?: string } };
    if (usesPaypalCard.value) {
      result = await paypalCheckout.confirmCardPayment();
    } else {
      result = isSubscription.value
        ? await checkout.confirmSubscription()
        : await checkout.confirmCardPayment();
    }

    if (result.error) {
      localError.value =
        result.error.message || t("don.checkoutApp.erreurPaiementGenerique");
      isLoading.value = false;
      return;
    }

    if (
      !usesPaypalCard.value &&
      !isSubscription.value &&
      currentPaymentIntentId.value
    ) {
      paymentSocket.joinPaymentRoom(currentPaymentIntentId.value);
    }
  } catch (error: unknown) {
    logger.error("Erreur critique lors du paiement:", error);
    localError.value = extractApiError(error).message;
    isLoading.value = false;
  }
};

// Moyens de paiement LOCAUX (Bancontact d'une association belge — étape B
// « autres pays »). Jamais en widget intégré (la banque redirige) ni pour un
// don mensuel. Absents pour une association française.
const {
  moyensLocaux,
  identifiantOfficiel,
  erreurMoyenLocal,
  erreurTitulaireMoyenLocal,
  erreurEmailMoyenLocal,
  payerParMoyenLocal,
  prelevement,
} = useMoyensLocauxDon({
  paymentProviders: donationData.paymentProviders,
  // Visible dans l'aperçu du tableau de bord (bouton inactif), comme Apple Pay.
  masquer: computed(() => isEmbed.value),
  // Don mensuel : seul le prélèvement SEPA reste proposé (02/11/2026).
  donMensuel: computed(() => isSubscription.value),
  // Formulaire de prélèvement de Stripe (IBAN + mandat).
  prelevementSepa: stripeDonation.prelevementSepa,
  // Champs du donateur contrôlés et signalés en rouge ; le message général
  // va sous le bouton du moyen, PAS sous la carte (Karim 21/10 : « j'ai mis
  // la carte mais elle est en rouge »).
  formulaireValide: () => {
    const requiresEmail = isFieldRequired("email");
    if (requiresEmail) validateEmail();
    if (!isValidForm.value || (requiresEmail && errors.email)) {
      validateAllFields();
      return false;
    }
    return true;
  },
  payer: stripeDonation.payerParMoyenLocal,
  isLoading,
  messageGenerique: () => t("don.checkoutApp.erreurPaiementGenerique"),
  messageFormulaireInvalide: () => t("don.checkoutApp.erreurValidation"),
  messageNomManquant: (moyen) => t(cleTexteTitulaire("nomEnHaut", moyen), { moyen: MOYENS_PAIEMENT_LOCAUX[moyen].nom }),
  messageNomInvalide: (moyen) => t(cleTexteTitulaire("nomRequis", moyen), { moyen: MOYENS_PAIEMENT_LOCAUX[moyen].nom }),
  messageEmailInvalide: () => t("don.moyensLocaux.emailInvalide"),
  // En direct : l'association allume ou éteint un moyen, le bouton suit (01/11/2026).
  enDirect: { socket: paymentSocket, organizationId: donationData.organizationId },
  // Link allumé / éteint par l'association : Stripe retire ou remet le bouton,
  // la page remonte son paiement express pour le voir aussitôt (02/11/2026).
  surChangementLink: () => checkout.rafraichirExpressCheckout(),
});

// ─────────────────────────────────────────────────────────────────────────
// Cycle de vie : dark mode désactivé, mount providers, (embed) resize + theme
// ─────────────────────────────────────────────────────────────────────────
const previousHadDark = ref(false);
const rootEl = ref<HTMLElement | null>(null);
let resizeObserver: ResizeObserver | null = null;
let embedMessageHandler: ((e: MessageEvent) => void) | null = null;

// (embed) Pousse la hauteur du contenu vers l'hôte pour l'auto-resize iframe.
function pushEmbedHeight() {
  if (!isEmbed.value || typeof window === "undefined") return;
  const target = resolvedHostOrigin.value;
  if (!target) return;
  const height = Math.ceil(
    rootEl.value?.getBoundingClientRect().height ??
      document.documentElement.scrollHeight,
  );
  window.parent.postMessage(
    { type: EMBED_MESSAGE.RESIZE, height },
    target,
  );
}

onMounted(async () => {
  if (typeof document !== "undefined") {
    const root = document.documentElement;
    previousHadDark.value = root.classList.contains("dark");
    root.classList.remove("dark");
  }

  if (!isPreviewMode.value || ssrPaymentData.value?.success) {
    await donationData.loadDonationData();
  }

  // Tracker le scan ou le clic (hors preview — donc aussi en embed).
  if (!isPreviewMode.value) {
    const isScan = route.query.scan === "1";
    const endpoint = isScan ? "scan" : "click";
    // Fire-and-forget : `trackHit` ne rejette jamais, pas d'`await`.
    void paymentLinksApi.trackHit(config.public.apiUrl, slug, endpoint);
  }

  // Ne pas initialiser Stripe/PayPal en preview (iframe dashboard). En embed
  // on initialise normalement (vrai paiement).
  if (isPreviewMode.value) return;

  // (embed) Canal postMessage : signale prêt + écoute theme/prefill de l'hôte.
  if (isEmbed.value && typeof window !== "undefined") {
    const target = resolvedHostOrigin.value;
    embedMessageHandler = (event: MessageEvent) => {
      // SÉCURITÉ — n'accepter que les messages de l'hôte validé.
      if (!target || event.origin !== target) return;
      const data = event.data;
      if (!data || typeof data !== "object") return;
      if (data.type === EMBED_MESSAGE.THEME) {
        const c = sanitizeCssColor(data.primaryColor);
        if (c) hostPrimaryColor.value = c;
      } else if (data.type === EMBED_MESSAGE.PREFILL) {
        if (typeof data.amount === "number" && data.amount > 0) {
          form.value.amount = String(data.amount);
        }
        if (typeof data.email === "string") form.value.donorEmail = data.email;
        if (typeof data.firstName === "string" || typeof data.lastName === "string") {
          form.value.donorName = [data.firstName, data.lastName]
            .filter(Boolean)
            .join(" ")
            .trim();
        }
      }
    };
    window.addEventListener("message", embedMessageHandler);
    if (target) {
      window.parent.postMessage({ type: EMBED_MESSAGE.READY }, target);
    }
  }

  paymentSocket.connect();
  paymentSocket.onPaymentConfirmed((data) => {
    if (hasRedirected) return;
    hasRedirected = true;

    // EMBED : on reste dans l'iframe, l'hôte gère l'après-don.
    if (isEmbed.value) {
      emitEmbedPaymentCompleted({
        transactionId: data.transactionId,
        paymentIntentId: data.paymentIntentId,
        amount: data.amount,
      });
      return;
    }

    // PAY : redirection classique vers la page de succès.
    router.push({
      path: "/donation-success",
      query: {
        payment_intent_id: data.paymentIntentId,
        transaction_id: data.transactionId,
        amount: data.amount,
        net_amount: finalAmount.value.toString(),
        include_fees: includeFees.value ? "true" : "false",
        donate_to_us:
          donateUs.value && donateToUsAmount.value > 0
            ? donateToUsAmount.value.toString()
            : "0",
        email: form.value.donorEmail,
      },
    });
  });

  await nextTick();

  if (usesPaypalCard.value) {
    try {
      await paypalCheckout.mountCardForm("paypal-cardfields-container", {
        onCardChange: () => { localError.value = null; },
      });
    } catch (e: unknown) {
      const info = extractApiError(e);
      logger.error("[Donate] PayPal mountCardForm error:", info.message);
    }
  } else {
    await checkout.mountCardForm("card-element", {
      type: "card-legacy",
      onCardChange: () => {
        localError.value = null;
        checkout.error.value = null;
      },
    });
  }
  if (!isSubscription.value || isPaypalOnlyMode.value) {
    await mountPrimaryWallets();
  }

  // (embed) Auto-resize : observe le contenu et pousse la hauteur à l'hôte.
  if (isEmbed.value && typeof window !== "undefined" && rootEl.value) {
    pushEmbedHeight();
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => pushEmbedHeight());
      resizeObserver.observe(rootEl.value);
    }
  }
});

// Re-calcul du tip Donoor quand le montant du don change (hors preview).
watch(
  () => paymentForm.form.value.amount,
  (newAmount) => {
    if (!isPreviewMode.value) {
      donateToUsAmount.value = getDonoorTipAmount(Number(newAmount) || 0);
    }
  },
);

// Re-mount le formulaire carte quand isSubscription change ET que ça modifie
// le provider primaire (usesPaypalCard). Cf. /pay historique.
watch(isSubscription, async (newValue, oldValue) => {
  if (oldValue === newValue) return;
  await nextTick();

  if (usesPaypalCard.value) {
    try {
      await paypalCheckout.mountCardForm("paypal-cardfields-container", {
        onCardChange: () => { localError.value = null; },
      });
    } catch (e: unknown) {
      const info = extractApiError(e);
      logger.error("[Donate] PayPal mountCardForm re-mount error:", info.message);
    }
  } else {
    await checkout.mountCardForm("card-element", {
      type: "card-legacy",
      onCardChange: () => {
        localError.value = null;
        checkout.error.value = null;
      },
    });
  }

  if (!isSubscription.value || isPaypalOnlyMode.value) {
    await mountPrimaryWallets();
  }
});

onBeforeUnmount(() => {
  if (typeof document !== "undefined" && previousHadDark.value) {
    document.documentElement.classList.add("dark");
  }
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
  if (embedMessageHandler && typeof window !== "undefined") {
    window.removeEventListener("message", embedMessageHandler);
    embedMessageHandler = null;
  }
});
</script>

<template>
  <div ref="rootEl" class="relative">
    <!-- Langue de la page, en haut à droite sur ordinateur ; sur mobile,
         sur la ligne du nom de l'association. Aussi dans l'aperçu du
         tableau de bord (Karim 01/11/2026). -->
    <div v-if="!isEmbed" class="hidden lg:block absolute top-3 right-3 z-30">
      <SelecteurLangue variante="clair" sens="bas" />
    </div>
    <!-- Cascade d'erreurs unifiée. -->
    <DonateErrorStates
      v-if="!pageLoading"
      :org-risk-status="donationData.organizationRiskStatus.value"
      :org-risk-reason="donationData.organizationRiskReason.value"
      :organization-name="donationData.organizationName.value"
      :is-entity-invalid="!isQrCodeValid"
      invalid-entity-kind="payment-link"
      :invalid-title="errorTitle"
      :invalid-message="qrCodeError"
      :no-payment-provider-active="!donationData.canAcceptPayments.value"
      :stripe-init-error="stripeInitError"
      :stripe-error-message="stripeErrorMessage"
      :is-paypal-only-mode="isPaypalOnlyMode"
      @reload="reloadPage"
    />

    <!-- Modal d'erreur pour les méthodes WALLET (PayPal jaune, Apple/Google Pay). -->
    <Teleport v-if="walletError" to="body">
      <div
        class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
        @click.self="dismissWalletError"
      >
        <div
          class="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6"
          role="alertdialog"
          aria-labelledby="wallet-error-title"
        >
          <div class="flex items-start gap-3 mb-4">
            <!-- Rond de couleur PLEINE, icône blanche (règle du 22/10). -->
            <div class="flex-shrink-0 w-10 h-10 rounded-full bg-red-500 flex items-center justify-center">
              <UIcon name="i-lucide-x" class="w-5 h-5 text-white" />
            </div>
            <div class="flex-1 pt-1">
              <h3
                id="wallet-error-title"
                class="text-base font-semibold text-gray-900 dark:text-white"
              >
                {{ t('don.checkoutApp.paiementRefuseTitre') }}
              </h3>
              <p class="mt-2 text-sm text-gray-600 dark:text-gray-300">
                {{ walletError }}
              </p>
            </div>
          </div>
          <div class="flex justify-end">
            <button
              type="button"
              class="h-10 px-5 text-sm font-semibold text-white bg-gray-900 hover:bg-gray-800 rounded-xl transition-colors"
              @click="dismissWalletError"
            >
              {{ t('don.checkoutApp.jaiCompris') }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <DonationPageSkeleton v-if="pageLoading" />

    <!-- Contenu principal — commun Stripe ET PayPal-only. -->
    <div
      v-if="!pageLoading && isQrCodeValid && (!stripeInitError || isPaypalOnlyMode)"
      class="min-h-screen"
      :style="{
        '--payment-primary': primaryColor || '#3B82F6',
        '--payment-primary-hover': darkenColor(primaryColor || '#3B82F6', 15),
        backgroundColor: backgroundColor || '#FFFFFF',
      }"
    >
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mx-auto">
        <!-- Colonne de gauche - Récapitulatif -->
        <div class="order-1 px-4 sm:px-6 lg:pl-15 xl:pl-60 pr-5 py-2 lg:py-14">
          <div class="sticky top-8">
            <!-- Desktop -->
            <div class="hidden lg:block space-y-6">
              <div class="flex items-center mb-4">
                <div
                  class="flex items-center px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700"
                >
                  <img
                    v-if="organizationLogoUrl"
                    :src="organizationLogoUrl"
                    :alt="donationInfo.organization"
                    class="w-4 h-4 mr-2 object-contain rounded-full"
                  />
                  <UIcon v-else name="i-lucide-home" class="w-4 h-4 mr-2" />
                  {{ donationInfo.organization }}
                </div>
                <div
                  v-if="isDev"
                  class="ml-2 px-3 py-1 bg-indigo-600 text-white rounded-full text-sm"
                >
                  Environnement de test
                </div>
              </div>

              <PayHero
                :banner-url="bannerUrl"
                :custom-title="customTitle"
                :custom-subtitle="customSubtitle"
                :message="messageQrCode"
                :display-amount="displayAmount"
                :is-subscription="isSubscription"
                :subscription-suffix="subscriptionSuffix"
                :organization-name="donationInfo.organization"
                variant="desktop"
              />

              <DonateFiscalBanner
                v-if="showFiscalInfo"
                class="block"
                :final-amount="finalAmount"
                :format-amount="formatAmount"
                :is-subscription="isSubscription"
              />

              <PaySummaryDetails
                v-model:message="form.message"
                :organization-name="donationInfo.organization"
                :final-amount="finalAmount"
                :detail-association="detailAssociation"
                :final-amount-with-discount="finalAmountWithDiscount"
                :display-amount="displayAmount"
                :format-amount="formatAmount"
                :include-fees="includeFees"
                :stripe-fees-amount="stripeFeesAmount"
                :donoor-fees-amount="donoorFeesAmount"
                :provider-label="usesPaypalCard ? 'paypal' : 'stripe'"
                :donate-us="donateUs"
                :donate-to-us-amount="donateToUsAmount"
                :is-subscription="isSubscription"
                :subscription-suffix="subscriptionSuffix"
                :show-goal-progress="showGoalProgress"
                :goal-amount="goalAmount"
                :raised-amount="raisedAmount"
                :primary-color="primaryColor"
                :days-remaining="daysRemaining"
                :is-message-enabled="isMessageEnabled"
              />
            </div>

            <!-- Mobile -->
            <div class="lg:hidden space-y-4 pt-8">
              <!-- Nom de l'association et langue sur la même ligne : le nom
                   se coupe (« … ») s'il est trop long. -->
              <div class="flex items-center gap-2">
                <div
                  class="inline-flex items-center min-w-0 px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700"
                >
                  <img
                    v-if="organizationLogoUrl"
                    :src="organizationLogoUrl"
                    :alt="donationInfo.organization"
                    class="w-4 h-4 mr-2 object-contain rounded-full shrink-0"
                  />
                  <UIcon v-else name="i-lucide-home" class="w-4 h-4 mr-2 shrink-0" />
                  <span class="truncate">{{ donationInfo.organization }}</span>
                </div>
                <div v-if="!isEmbed" class="ml-auto shrink-0">
                  <SelecteurLangue variante="clair" sens="bas" />
                </div>
              </div>

              <PayHero
                :banner-url="bannerUrl"
                :custom-title="customTitle"
                :custom-subtitle="customSubtitle"
                :message="messageQrCode"
                :display-amount="displayAmount"
                :is-subscription="isSubscription"
                :subscription-suffix="subscriptionSuffix"
                :organization-name="donationInfo.organization"
                variant="mobile"
              />

              <div v-if="showGoalProgress" class="shadow-md rounded-xl">
                <DonateGoalProgress
                  v-if="goalAmount"
                  :raised-amount="raisedAmount"
                  :goal-amount="goalAmount"
                  :primary-color="primaryColor"
                  :format-amount="formatAmount"
                  :days-remaining="daysRemaining"
                />
                <div v-else class="p-4 bg-gray-50 rounded-xl">
                  <div class="flex justify-between items-end">
                    <span class="text-base font-bold text-gray-900">
                      {{ formatAmount(raisedAmount) }}
                    </span>
                    <span class="text-gray-500 text-sm">{{ t('don.checkoutApp.collectes') }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Colonne de droite - Formulaire de paiement. ALIGNÉE EN HAUT comme
             la colonne de gauche : centrée verticalement, elle descendait au
             milieu d'un écran haut pendant que le récapitulatif restait en
             haut (Karim 2026-10-12). Même alignement que /c/…/payment et
             /donate/…. -->
        <div
          class="order-2 px-4 sm:px-6 lg:px-14 pb-6 lg:py-14 md:shadow-2xl lg:min-h-screen"
        >
          <form
            class="max-w-md mx-auto lg:mx-0 space-y-4"
            @submit.prevent="handleSubmit"
          >
            <!-- Mode aperçu (dashboard) : placeholder statique Apple/Google Pay. -->
            <div
              v-if="isPreviewMode && (!isSubscription || isPaypalOnlyMode)"
              class="min-h-[48px] flex gap-2"
            >
              <button
                type="button"
                disabled
                class="flex-1 h-[44px] bg-black text-white rounded-md flex items-center justify-center gap-2 text-sm font-medium"
              >
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" /></svg>
                Pay
              </button>
              <button
                type="button"
                disabled
                class="flex-1 h-[44px] bg-black text-white rounded-md flex items-center justify-center gap-2 text-sm font-medium"
              >
                <svg class="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" /><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" /><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" /><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" /></svg>
                Pay
              </button>
            </div>

            <!-- Mode normal : composant unifié Express Checkout. -->
            <DonateExpressCheckoutSection
              v-else
              :stripe-checkout="checkout"
              :paypal-checkout="paypalCheckout"
              :payment-form="paymentForm"
              :is-paypal-only-mode="isPaypalOnlyMode"
              :show-pay-pal-alternative="showPayPalAlternative"
              :is-subscription="isSubscription"
              :uses-paypal-wallets="usesPaypalWallets"
            />

            <!-- iDEAL | Wero (Pays-Bas), Bancontact (Belgique) : paiement express, sous
                 Apple Pay / Google Pay (Karim 01/11/2026). Rien pour la France. -->
            <DonateMoyensLocaux
              :moyens="moyensLocaux"
              :nom-donateur="form.isAnonymous ? '' : form.donorName"
              :nom-demande-en-haut="champNomAffiche(customFieldsWithoutMessage, form.isAnonymous)"
              :anonyme="form.isAnonymous"
              :email="form.donorEmail"
              :email-requis="isFieldRequired('email')"
              :montant="finalAmount > 0 ? formatAmount(finalAmount) : null"
              :is-loading="isLoading"
              :is-preview-mode="isPreviewMode"
              :erreur="erreurMoyenLocal"
              :erreur-nom="erreurTitulaireMoyenLocal"
              :erreur-email="erreurEmailMoyenLocal"
              :prelevement="prelevement"
              :mensuel="isSubscription"
              @update:email="(v: string) => (form.donorEmail = v)"
              @update:nom-donateur="(v: string) => (form.donorName = v)"
              @payer="payerParMoyenLocal"
            />

            <!-- Options (Couvrir les frais + Soutenir Donoor) -->
            <div data-preview-id="options-group">
              <DonateOptionsGroup
                v-model:include-fees="includeFees"
                v-model:donate-to-us-amount="donateToUsAmount"
                :fees-amount="feesAmount"
                :final-amount="finalAmount"
                :fees-text="feesText"
                :format-amount="formatAmount"
                :donate-us="donateUs"
                :is-premium-plan="isPremiumPlan"
                :is-subscription="isSubscription"
                :is-preview-mode="isPreviewMode"
                :show-fees-option="showFeesOption"
                :provider-label="usesPaypalCard ? 'paypal' : 'stripe'"
                :show-fiscal-info="showFiscalInfo"
                fiscal-in-sidebar-on-desktop
                :organization-name="donationInfo.organization"
              />
            </div>

            <!-- Type de don (unique/récurrent). Masqué en PayPal-only. -->
            <div
              v-if="subscriptionsEnabled && !isPaypalOnlyMode"
              data-preview-id="donation-type"
            >
              <DonateDonationType
                v-model:donation-type="donationType"
                v-model:subscription-period="subscriptionPeriod"
                :subscription-periods="subscriptionPeriods"
                :is-subscription="isSubscription"
                :primary-color="primaryColor"
              />
            </div>

            <!-- Sélection du montant -->
            <div data-preview-id="amount-selector">
              <DonateAmountSelector
                data-montant-don
                v-model:amount="form.amount"
                :suggested-amounts="suggestedAmounts"
                :is-subscription="isSubscription"
                :subscription-period="subscriptionPeriod"
                :format-amount="formatAmount"
                :touched="touched"
                :errors="errors"
                :button-style="buttonStyle"
                :primary-color="primaryColor"
                @validate="validateAmount"
              />
              <DonateImpactOptions
                v-model:amount="form.amount"
                class="mt-4"
                :options="impactOptions"
                :format-amount="formatAmount"
                :primary-color="primaryColor"
                :period-suffix="isSubscription ? subscriptionSuffix : null"
              />
            </div>

            <!-- Champs CB intégrés — Stripe Elements ou PayPal CardFields. -->
            <DonateCardElement
              :is-initialized="
                usesPaypalCard
                  ? (paypalCheckout.cardMounted.value || !!paypalCheckout.cardFormError.value)
                  : (checkout.cardMounted.value || !!checkout.cardFormError.value)
              "
              :is-preview-mode="isPreviewMode"
              :provider="usesPaypalCard ? 'paypal' : 'stripe'"
              :mount-error-code="
                usesPaypalCard ? (paypalCheckout.cardFormError.value?.code ?? null) : null
              "
              :card-error="globalError"
            />

            <!-- Formulaire donateur -->
            <div data-preview-id="donor-form">
              <DonateDonorForm
                v-model:donor-email="form.donorEmail"
                v-model:donor-name="form.donorName"
                v-model:is-anonymous="form.isAnonymous"
                v-model:message="form.message"
                v-model:custom-field-values="customFieldValues"
                :touched="touched"
                :errors="errors"
                :show-message-field="isMessageEnabled"
                :custom-fields="customFieldsWithoutMessage"
                @validate-email="validateEmail"
                @validate-name="validateName"
              />
            </div>

            <!-- Bouton "Donner" — Stripe ET PayPal. Pas de « Payez en toute
                 sécurité à … » sous le bouton : la ligne « Paiement sécurisé
                 par Stripe ou PayPal… » plus bas le dit déjà (Karim 02/11/2026,
                 « trop d'info au pied de page »). -->
            <div data-preview-id="submit-button">
              <DonateSubmitButton
                :is-valid-form="isValidForm"
                :is-loading="isLoading"
                :is-subscription="isSubscription"
                :global-error="globalError"
                :organization-name="donationInfo.organization"
                :is-preview-mode="isPreviewMode"
                :submit-text="submitText"
                :primary-color="primaryColor"
                :button-style="buttonStyle"
                hide-secure-note
                @validate-all="validateAllFields"
              />
            </div>

            <!-- Mentions légales -->
            <i18n-t
              keypath="don.checkoutApp.mentionsLegales"
              tag="p"
              class="mt-4 text-xs text-gray-500 text-center"
            >
              <template #lien>
                <a :href="localePath({ path: '/terms' })" class="underline hover:text-gray-700">{{ t('don.checkoutApp.mentionsLegalesLien') }}</a>
              </template>
            </i18n-t>
            <!-- Numéro officiel de l'association, exigé par iDEAL | Wero quand
                 elle le propose : en bas, avec « Paiement sécurisé par… »
                 (Karim 02/11/2026), pas sous le bouton. -->
            <p v-if="identifiantOfficiel" class="mt-1 text-xs text-gray-500 text-center">
              {{ donationInfo.organization }} · <span data-testid="identifiant-officiel">{{ identifiantOfficiel }}</span>
            </p>

            <!-- Badge « Propulsé par Donoor » (marque de la plateforme).
                 Masqué en mode embed : le widget porte déjà sa propre pilule. -->
            <div v-if="!isEmbed" class="mt-5">
              <DonatePoweredByDonoor />
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
